import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../api/client';
import './MarketPricesPage.css';

const CHART_COLORS = ['#B8860B', '#2E7D32', '#1565C0', '#E74C3C', '#8E24AA', '#00838F', '#E65100', '#5E35B1'];

function MarketPricesChart() {
  const [selectedModels, setSelectedModels] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: availableModels = [] } = useQuery({
    queryKey: ['market-models'],
    queryFn: () => api.get('/market-prices/models').then(r => r.data),
  });

  const modelsParam = selectedModels.map(m => `${m.brand}:${m.model}`).join(',');
  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['market-history', modelsParam],
    queryFn: () => api.get(`/market-prices/history?models=${encodeURIComponent(modelsParam)}`).then(r => r.data),
    enabled: selectedModels.length > 0,
  });

  const chartData = useMemo(() => {
    if (!historyData?.series?.length) return [];
    const weekMap = {};
    historyData.series.forEach((s, i) => {
      const key = `${s.brand} ${s.model}`;
      s.weekly.forEach(w => {
        if (!w.week) return;
        const weekLabel = new Date(w.week).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        if (!weekMap[w.week]) weekMap[w.week] = { week: w.week, label: weekLabel };
        weekMap[w.week][key] = w.price_ttc;
      });
    });
    return Object.values(weekMap).sort((a, b) => a.week.localeCompare(b.week));
  }, [historyData]);

  const seriesKeys = historyData?.series?.map(s => `${s.brand} ${s.model}`) || [];

  const toggleModel = (brand, model) => {
    setSelectedModels(prev => {
      const exists = prev.find(m => m.brand === brand && m.model === model);
      if (exists) return prev.filter(m => !(m.brand === brand && m.model === model));
      if (prev.length >= 8) return prev;
      return [...prev, { brand, model }];
    });
  };

  const grouped = useMemo(() => {
    const term = search.toLowerCase();
    const map = {};
    availableModels.forEach(m => {
      if (term && !`${m.brand} ${m.model}`.toLowerCase().includes(term)) return;
      if (!map[m.brand]) map[m.brand] = [];
      map[m.brand].push(m);
    });
    return map;
  }, [availableModels, search]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="mp-tooltip">
        <div className="mp-tooltip-date">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="mp-tooltip-row">
            <span className="mp-tooltip-dot" style={{ background: p.color }} />
            <span className="mp-tooltip-name">{p.name}</span>
            <span className="mp-tooltip-price">&euro;{p.value?.toLocaleString('fr-FR')}</span>
          </div>
        ))}
        {historyData?.series && (
          <div className="mp-tooltip-grades">
            {historyData.series.map((s, i) => {
              const key = `${s.brand} ${s.model}`;
              if (!payload.find(p => p.name === key)) return null;
              return (
                <div key={i} className="mp-tooltip-grade-group">
                  <div className="mp-tooltip-model-header">
                    {s.image && <img src={s.image} alt="" className="mp-tooltip-img" />}
                    <div>
                      <div className="mp-tooltip-grade-title" style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}>{s.brand}</div>
                      <div className="mp-tooltip-model-name">{s.model}</div>
                    </div>
                  </div>
                  {Object.entries(s.by_grade).map(([grade, data]) => (
                    <div key={grade} className="mp-tooltip-grade-row">
                      <span className="mp-tooltip-grade-badge">{grade}</span>
                      <span>&euro;{data.price_ttc?.toLocaleString('fr-FR')}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="market-prices-section">
      <div className="mp-model-selector" onMouseLeave={() => setDropdownOpen(false)}>
        <button className="input mp-dropdown-btn" onClick={() => setDropdownOpen(o => !o)}>
          {selectedModels.length === 0
            ? 'Select models to track\u2026'
            : `${selectedModels.length} model${selectedModels.length > 1 ? 's' : ''} selected`}
          <span className="mp-chevron">{dropdownOpen ? '\u25B2' : '\u25BC'}</span>
        </button>
        {dropdownOpen && (
          <div className="mp-dropdown">
            <div className="mp-dropdown-search">
              <input
                className="input"
                placeholder="Search brand or model\u2026"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            {Object.entries(grouped).map(([brand, models]) => (
              <div key={brand}>
                <div className="mp-dropdown-brand">{brand}</div>
                {models.map(m => {
                  const isSelected = selectedModels.some(s => s.brand === m.brand && s.model === m.model);
                  const idx = selectedModels.findIndex(s => s.brand === m.brand && s.model === m.model);
                  return (
                    <label key={`${m.brand}:${m.model}`} className="mp-dropdown-option">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleModel(m.brand, m.model)}
                      />
                      {m.image && <img src={m.image} alt="" className="mp-dropdown-img" />}
                      <span className="mp-dropdown-name">{m.model}</span>
                      {isSelected && <span className="mp-dropdown-dot" style={{ background: CHART_COLORS[idx % CHART_COLORS.length] }} />}
                    </label>
                  );
                })}
              </div>
            ))}
            {Object.keys(grouped).length === 0 && (
              <div className="mp-dropdown-empty">No models match your search</div>
            )}
          </div>
        )}
      </div>

      {selectedModels.length > 0 && (
        <div className="mp-selected-tags">
          {selectedModels.map((m, i) => (
            <button
              key={`${m.brand}:${m.model}`}
              className="mp-tag"
              style={{ borderColor: CHART_COLORS[i % CHART_COLORS.length], color: CHART_COLORS[i % CHART_COLORS.length] }}
              onClick={() => toggleModel(m.brand, m.model)}
            >
              {m.brand} {m.model} &times;
            </button>
          ))}
        </div>
      )}

      {selectedModels.length > 0 && (
        <div className="mp-chart-container">
          {loadingHistory ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888' }} />
                <YAxis tick={{ fontSize: 11, fill: '#888' }} tickFormatter={v => `\u20AC${v.toLocaleString('fr-FR')}`} width={80} domain={[300, 'auto']} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {seriesKeys.map((key, i) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={key}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 40 }}>No data available for selected models.</p>
          )}
        </div>
      )}

      {selectedModels.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 12 }}>
          Select models above to see historical recommended prices.
        </p>
      )}
    </div>
  );
}

export default function MarketPricesPage() {
  return (
    <div className="market-prices-page">
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 400, marginBottom: 32 }}>Market Prices</h1>
      <MarketPricesChart />
    </div>
  );
}
